import type { UserEntitlementView } from '@steado/admin-contract/user-detail';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GiftIcon } from 'lucide-react';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';

import { grantEntitlement, revokeEntitlement } from './user-detail-query';

interface EntitlementActionsProps {
  readonly userId: string;
  readonly subscription: UserEntitlementView;
}

/**
 * The console's two levers over one person's access, in the record header.
 *
 * Beside the name rather than eight cards down the Profile tab, because it is
 * what somebody arriving from a support thread came to do. The Profile tab's
 * Entitlement card still states where the entitlement came from and when it
 * ends; what it no longer carries is a second copy of these buttons.
 *
 * A grant writes a `promotional` subscription row — the testing-phase way to
 * make someone Premium, and the only premium path on web until Web Billing
 * ships. It does not touch any store subscription: a store user granted over
 * keeps their store billing, and RevenueCat's next event reclaims the row.
 *
 * **The design also draws a Deactivate button here. There is no route behind
 * it**: an account is soft-deleted through the app's own deletion flow, and
 * `/admin/users/:id` exposes no way to do it. A button that 404s is worse than
 * one that is absent, so it is absent.
 */
export function EntitlementActions({ userId, subscription }: EntitlementActionsProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const expiryId = useId();
  const [expiresAt, setExpiresAt] = useState('');
  const [dialog, setDialog] = useState<'none' | 'grant' | 'revoke'>('none');

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId, 'detail'] });

  const grant = useMutation({
    mutationFn: () =>
      grantEntitlement(api, userId, expiresAt === '' ? {} : { expiresAt: new Date(expiresAt).toISOString() }),
    onSuccess: () => {
      setExpiresAt('');
      setDialog('none');
      invalidate();
    },
    retry: false,
  });

  const revoke = useMutation({
    mutationFn: () => revokeEntitlement(api, userId),
    onSuccess: () => {
      setDialog('none');
      invalidate();
    },
    retry: false,
  });

  const premium = subscription.entitlement === 'premium';

  return (
    <>
      {premium ? (
        <Button
          variant="outline"
          size="sm"
          disabled={!can.mutate || revoke.isPending}
          title={can.mutate ? undefined : 'Revoking Premium requires the admin role.'}
          onClick={() => {
            setDialog('revoke');
          }}
          className="text-err-fg hover:text-err-fg"
        >
          <GiftIcon aria-hidden="true" className="size-3.5" />
          Revoke premium
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={!can.mutate || grant.isPending}
          title={can.mutate ? undefined : 'Granting Premium requires the admin role.'}
          onClick={() => {
            setDialog('grant');
          }}
        >
          <GiftIcon aria-hidden="true" className="size-3.5" />
          Grant premium
        </Button>
      )}

      {dialog === 'grant' ? (
        <ConfirmDialog
          title="Grant Premium"
          body={
            <div className="flex flex-col gap-3">
              <p>
                No store purchase is involved; the app sees it on its next billing fetch. It is recorded
                against your console account, and audited.
              </p>
              <div className="flex flex-wrap items-center gap-2.5">
                <Label htmlFor={expiryId} className="text-muted-foreground">
                  Expires
                </Label>
                <DateTimePicker
                  id={expiryId}
                  value={expiresAt}
                  onChange={setExpiresAt}
                  placeholder="No expiry"
                />
                <span className="text-[13px] text-muted-foreground">empty = until revoked</span>
              </div>
            </div>
          }
          confirmLabel="Grant"
          isPending={grant.isPending}
          {...(grant.isError ? { error: grant.error.message } : {})}
          onConfirm={() => {
            grant.mutate();
          }}
          onCancel={() => {
            setDialog('none');
            grant.reset();
          }}
        />
      ) : null}

      {dialog === 'revoke' ? (
        <ConfirmDialog
          title="Revoke Premium"
          body={
            subscription.store === 'promotional' || subscription.store === null
              ? 'This user drops to the free plan immediately.'
              : 'This user has a store subscription. Revoking removes access now, but their next ' +
                'store renewal will grant it back. Cancel the store subscription too if this ' +
                'should stick.'
          }
          confirmLabel="Revoke"
          destructive
          isPending={revoke.isPending}
          {...(revoke.isError ? { error: revoke.error.message } : {})}
          onConfirm={() => {
            revoke.mutate();
          }}
          onCancel={() => {
            setDialog('none');
            revoke.reset();
          }}
        />
      ) : null}
    </>
  );
}
